// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import xOrderInformationStyles from "@conklin/App/SharedStyles/MyAccountPages/Details/OrderInformationStyles";
import { orderInformationStyles } from "@conklin/Overrides/Widgets/OrderApprovalDetails/OrderApprovalDetailsOrderInformation";
import { color, mergeWithCss, xScreen } from "@conklin/XDL/Utilities";
import { css } from "styled-components";

const orderApprovalInformationStyling = css`
    padding: 0 22px 20px 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

orderInformationStyles.orderInformationGridContainer = {
    gap: 0,
    css: css`
        &&& {
            border-bottom: 1px solid ${color("common.border")};
            padding-bottom: 24px;
            margin-bottom: 24px;
        }
    `,
};

orderInformationStyles.mainInformationGridContainer = {
    gap: 0,
    css: css`
        &&& {
            border-bottom: 1px solid ${color("common.border")};
            padding-bottom: 4px;
            margin-bottom: 24px;
        }
    `,
};

orderInformationStyles.mainInformationGridItem = {
    width: 12,
};

orderInformationStyles.orderNumberTitle = {
    ...xOrderInformationStyles.label,
};

orderInformationStyles.orderDateTitle = {
    ...xOrderInformationStyles.label,
};

orderInformationStyles.poNumberTitle = {
    ...xOrderInformationStyles.label,
};

orderInformationStyles.statusTitle = {
    ...xOrderInformationStyles.label,
};

orderInformationStyles.orderNumberText = {
    ...xOrderInformationStyles.value,
};

orderInformationStyles.orderDateText = {
    ...xOrderInformationStyles.value,
};

orderInformationStyles.poNumberText = {
    ...xOrderInformationStyles.value,
};

orderInformationStyles.statusText = {
    ...xOrderInformationStyles.value,
};

orderInformationStyles.orderNumberGridItem = {
    width: [6, 6, 6, 4, 4],
    css: orderApprovalInformationStyling,
};

orderInformationStyles.orderDateGridItem = {
    width: [6, 6, 6, 4, 4],
    css: orderApprovalInformationStyling,
};

orderInformationStyles.poNumberGridItem = {
    width: [6, 6, 6, 4, 4],
    css: orderApprovalInformationStyling,
};

orderInformationStyles.statusGridItem = {
    width: [6, 6, 6, 4, 4],
    css: orderApprovalInformationStyling,
};

orderInformationStyles.notesTitle = {
    ...xOrderInformationStyles.label,
};

orderInformationStyles.notesDescription = {
    ...xOrderInformationStyles.value,
};

orderInformationStyles.orderApprovalDetailsShippingInformation = {
    shippingInformationTitle: {
        css: css`
            display: none;
        `,
    },
    shippingAddressTitle: {
        ...mergeWithCss(xOrderInformationStyles.address?.informationTitle, {
            as: "h3",
            css: css`
                margin-bottom: 0;
                padding-bottom: 8px;
            `,
        }),
    },
    shippingInformationCarrierGridItem: {
        width: 12,
        css: orderApprovalInformationStyling,
    },
    shippingInformationDateRequestedGridItem: {
        width: 12,
        css: orderApprovalInformationStyling,
    },
    shippingInformationServiceGridItem: {
        width: 12,
        css: css`
            display: flex;
            flex-direction: column;
            gap: 8px;
        `,
    },
    shippingCarrierTitle: {
        ...xOrderInformationStyles.label,
    },
    shippingCarrierText: {
        ...xOrderInformationStyles.value,
    },
    shippingDateRequestedTitle: {
        ...xOrderInformationStyles.label,
    },
    shippingDateRequestedText: {
        ...xOrderInformationStyles.value,
    },
    shippingServiceTitle: {
        ...xOrderInformationStyles.label,
    },
    shippingServiceText: {
        ...xOrderInformationStyles.value,
    },
    shippingInformationGridContainer: {
        gap: 0,
        css: css`
            ${xScreen("lte", "md")} {
                margin-bottom: 0;
            }
        `,
    },
    shippingOtherInformationGridContainer: {
        gap: 0,
        css: css`
            ${xScreen("lte", "md")} {
                margin-bottom: 0;
            }
        `,
    },
    shippingInformationAddressGridItem: {
        width: [12, 6, 6, 6, 6],
        css: css`
            flex-direction: column;
        `,
    },
    shippingOtherInformationGridItem: {
        width: [12, 6, 6, 6, 6],
    },
};

orderInformationStyles.orderApprovalDetailsBillingInformation = {
    billingInformationGridContainer: {
        gap: 0,
    },
    billingInformationTitle: {
        css: css`
            display: none;
        `,
    },
    billingAddressTitle: {
        ...mergeWithCss(xOrderInformationStyles.address?.informationTitle, {
            as: "h3",
            css: css`
                padding-bottom: 8px;
                margin-bottom: 0;
            `,
        }),
    },
    billingCreditCardAddressTitle: {
        ...mergeWithCss(xOrderInformationStyles.address?.informationTitle, {
            as: "h3",
            css: css`
                padding-bottom: 8px;
                margin-bottom: 0;
            `,
        }),
    },
};

orderInformationStyles.billingInformationGridItem = {
    width: [12, 12, 12, 6, 6],
    css: css`
        order: 1;
    `,
};

orderInformationStyles.shippingInformationGridItem = {
    width: [12, 12, 12, 6, 6],
    css: css`
        order: 2;
        ${xScreen("lte", "md")} {
            order: 4;
        }
    `,
};

orderInformationStyles.addressesTitle = {
    ...xOrderInformationStyles.address?.heading,
};

orderInformationStyles.addressesGridItem = {
    width: 12,
    css: css`
        flex-direction: row;
        padding-top: 0;
        padding-bottom: 0;
    `,
};

orderInformationStyles.notesGridItem = {
    width: 12,
    css: css`
        order: 3;
        ${xScreen("lte", "md")} {
            padding-bottom: 24px;
        }
    `,
};
